import axios from "axios";
import qs from "qs";
import offlineQueue from "./offlineQueue";

/**
 * Offline-queueable technician actions (Flow 7) share this shape: the raw
 * `send*` function does the actual network call given { orderId, token,
 * ...body }, and is registered in OFFLINE_DISPATCH so offlineQueue can
 * replay it later using nothing but the record it saved — no closures, since
 * IndexedDB can't store those.
 */
function buildFormData(body) {
  const form = new FormData();
  Object.entries(body).forEach(([key, value]) => {
    if (key === "photos" && Array.isArray(value)) {
      value.forEach((file) => form.append("order_document", file));
    } else if (Array.isArray(value)) {
      value.forEach((v) => form.append(key, v));
    } else if (value !== undefined && value !== null) {
      form.append(key, value);
    }
  });
  return form;
}

async function sendRespondToJob({ orderId, token, ...body }) {
  const response = await axios.post(
    `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${orderId}/installation/respond`,
    body,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return response.data;
}

async function sendSubmitChecklist({ orderId, token, ...body }) {
  const response = await axios.post(
    `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${orderId}/installation/checklist`,
    buildFormData(body),
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return response.data;
}

async function sendMarkIncomplete({ orderId, token, ...body }) {
  const response = await axios.post(
    `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${orderId}/installation/incomplete`,
    buildFormData(body),
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return response.data;
}

// activateUnit is deliberately NOT offline-queued — verifying the code is a
// live check against the kiosk's real activation/payment status, so queueing
// and replaying it later would just mean acting on stale information.
export const OFFLINE_DISPATCH = {
  respondToJob: sendRespondToJob,
  submitChecklist: sendSubmitChecklist,
  markIncomplete: sendMarkIncomplete,
};

const apiService = {
  fetchDoctors: async (auth, page, limit, keyword, stage) => {
    try {
      const fields = [
        "clinics",
        "name",
        "mobile",
        "countryCode",
        "username",
        "validTill",
        "websiteUrl",
        "logoUrl",
        "gender",
        "createdAt",
        "onboardingStatus",
        "mediaDriveLink",
        "status",
        "engageAccess",
        "_id",
      ];

      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/doctors`,
        {
          params: { page, limit, keyword, fields, stage },
          headers: { Authorization: `Bearer ${auth}` },
          paramsSerializer: (params) =>
            qs.stringify(params, { arrayFormat: "repeat" }), // 👈 key line
        },
      );

      const currentData = response.data.data;
      const count = response.data.totalItems;

      const mappedDoctors = currentData.map((doctor) => {
        const validTill = new Date(doctor.validTill);
        const formattedValidTill = isNaN(validTill)
          ? "Invalid Date"
          : validTill.toISOString().split("T")[0];

        let status;
        if (formattedValidTill === "Invalid Date") {
          status = "Invalid";
        } else {
          const today = new Date().toISOString().split("T")[0];
          status = formattedValidTill < today ? "Inactive" : "Active";
        }

        return {
          id: doctor._id,
          name: doctor.name,
          mobile: doctor.mobile,
          username: doctor.username,
          countryCode: doctor.countryCode,
          gender: doctor.gender,
          validTill: formattedValidTill,
          status: status,
          inactivity: doctor.status,
          engageAccess: doctor.engageAccess,
        };
      });

      return { mappedDoctors, count };
    } catch (error) {
      console.error("Error fetching doctors:", error);
      throw error;
    }
  },

  fetchKiosk: async (auth, page, limit, keyword) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/kiosks`,
        {
          params: { page, limit, keyword },
          headers: { Authorization: `Bearer ${auth}` },
        },
      );
      const count = response.data.totalItems;

      const mappedDoctors = response.data.data.map((kiosk) => {
        const activationCode = kiosk.activation?.activationCode || "N/A";
        const activationStatus = kiosk.activation?.activationStatus || "NA";
        const shipStatus = kiosk.logistics?.shipment?.status || "NA";
        const validity = kiosk.activation?.activationValidity || "NA";

        const formatDate = (validity) => {
          if (validity) {
            const date = new Date(validity);
            if (!isNaN(date.getTime())) {
              return date.toISOString().split("T")[0];
            } else {
              console.warn(`Invalid `, validity);
            }
          } else {
            console.warn(`Missing `);
          }
          return "";
        };
        return {
          id: kiosk._id,
          doctorName: kiosk.doctor,
          clinicName: kiosk.clinic?.name || "NA",
          name: kiosk.name,
          doctor: kiosk.doctor,
          location: kiosk.location,
          inactivity: kiosk.status,
          code: activationCode,
          status: activationStatus,
          Activationvalidity: formatDate(validity) || "NA",
          shipStatus,
          activeInLast7Days: kiosk.activeInLast7Days,
          activeInLast30Days: kiosk.activeInLast30Days,
          activeInLast60Days: kiosk.activeInLast60Days,
          activeInLast90Days: kiosk.activeInLast90Days,
        };
      });

      return { mappedDoctors, count };
    } catch (error) {
      console.error("Error fetching doctors:", error);
      throw error;
    }
  },
  updateDoctor: async (formData, token) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/doctors/update`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
  fetchUsers: async (
    auth,
    page,
    limit,
    doctorId = [],
    clinicId = [],
    keyword,
    testStartDate,
    testEndDate,
    sourceFilter = [],
    testType = "",
  ) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/getUsers`,
        {
          params: {
            page,
            limit,
            doctorId,
            clinicId,
            keyword,
            testStartDate,
            testEndDate,
            sourceFilter,
            ...(testType ? { testType } : {}),
          },
          headers: { Authorization: `Bearer ${auth}` },
        },
      );

      // Access correct structure
      const count = response.data.patients.filteredItemsCount;
      const users = response.data.patients.data;

      const mapped = users.map((user) => ({
        name: user.name,
        uid: user.uid,
        countryCode: user.countryCode,
        mobile: user.mobile,
        source: user.source,
        lastTestDate: user.lastTestDate,
        doctor: user.doctor,
        testDate: user.testDate,
      }));

      return { mapped, count };
    } catch (error) {
      console.error("Error fetching Users:", error);
      throw error;
    }
  },

  // Scans across patients. filters: { clinicIds, doctorIds, testType, startDate, endDate }
  listScans: async (auth, page, limit, filters = {}) => {
    try {
      const { clinicIds = [], doctorIds = [], testType, startDate, endDate } = filters;
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/scans`,
        {
          params: {
            page,
            limit,
            clinicId: clinicIds,
            doctorId: doctorIds,
            ...(testType ? { testType } : {}),
            ...(startDate ? { startDate } : {}),
            ...(endDate ? { endDate } : {}),
          },
          paramsSerializer: (params) =>
            qs.stringify(params, { arrayFormat: "repeat" }),
          headers: { Authorization: `Bearer ${auth}` },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching scans:", error);
      throw error;
    }
  },
  // Signed thumbnail URLs for each angle of one scan (hover preview).
  fetchScanImages: async (auth, testId) => {
    const response = await axios.get(
      `${process.env.REACT_APP_API_HOST_URL}/ops/v1/scans/${testId}/images`,
      { headers: { Authorization: `Bearer ${auth}` } },
    );
    return response.data.images;
  },
  fetchUserTests: async (userId, auth, page, limit, keyword, testType = "") => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/getUserTests`,
        {
          params: {
            userId,
            page,
            limit,
            keyword,
            ...(testType ? { testType } : {}),
          },
          headers: { Authorization: `Bearer ${auth}` },
        },
      );
      return response;
    } catch (error) {
      console.error("Error fetching tests:", error);
      throw error;
    }
  },
  deleteTest: async (auth, userId, testId) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/test/delete`,
        {
          userId,
          testId,
        },
        {
          headers: { Authorization: `Bearer ${auth}` },
        },
      );
      return response;
    } catch (error) {
      console.error("Error fetching tests:", error);
      throw error;
    }
  },
  getImage: async (auth, url) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/getImageFromUrl`,
        {
          headers: { Authorization: `Bearer ${auth}` },
          params: { url }, // query param
        },
      );
      return response.data.data;
    } catch (error) {
      console.error("Error fetching image:", error);
      throw error;
    }
  },
  getDayWiseData: async (auth, startDate, endDate) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/v1/analytics/get-day-wise-overall-analytics`,
        {
          params: { startDate, endDate },
          headers: { Authorization: `Bearer ${auth}` },
        },
      );
      return response;
    } catch (error) {
      console.error("Error fetching image:", error);
      throw error;
    }
  },
  getCounts: async (auth) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/getCounts`,
        {
          headers: { Authorization: `Bearer ${auth}` },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching counts:", error);
      throw error;
    }
  },

  getUnreadCount: async (auth) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/tags/unread-count`,
        {
          headers: { Authorization: `Bearer ${auth}` },
        },
      );
      return response.data.count;
    } catch (error) {
      console.error("Error fetching counts:", error);
      throw error;
    }
  },
  getOverAllMonthwise: async (auth, startDate, endDate) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/v1/analytics/get-month-wise-overall-analytics`,
        {
          params: { startDate, endDate },
          headers: { Authorization: `Bearer ${auth}` },
        },
      );
      return response;
    } catch (error) {
      console.error("Error fetching image:", error);
      throw error;
    }
  },

  fetchDoctorDetails: async (doctorId, token) => {
    if (!doctorId) {
      throw new Error("Doctor ID is required to fetch details");
    }
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/doctors/${doctorId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  fetchkioskDetails: async (kioskId, token) => {
    if (!kioskId) {
      return null;
    }
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/kiosks/${kioskId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching doctor details:", error);
      throw error;
    }
  },

  updateActivation: async ({ kioskId, activationStatus, validity, token }) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/activations/update`,
        {
          kioskId,
          activationStatus,
          validity,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      return response.data;
    } catch (error) {
      console.error("Error updating activation:", error);
      throw error;
    }
  },
  updateShipment: async ({
    kioskId,
    deliveryAddress,
    expectedDeliveryDate,
    actualDeliveryDate,
    status,
    clinicName,
    token,
  }) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/logistics/shipment/update`,
        {
          kioskId,
          deliveryAddress,
          expectedDeliveryDate,
          actualDeliveryDate,
          status,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      return response.data;
    } catch (error) {
      console.error("Error updating shipment:", error);
      throw error;
    }
  },

  createPayment: async ({ kioskId, totalAmount, currency, token }) => {
    if (!kioskId) {
      console.error("Missing kioskId");
      return null;
    }
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/payments/create`,
        {
          kioskId,
          totalAmount,
          currency,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
    } catch (error) {
      console.error("Error creating payment details:", error);
      throw error;
    }
  },

  fetchPaymentDetails: async (kioskId, token) => {
    if (!kioskId) {
      return null;
    }
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/payments/${kioskId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      return response.data;
    } catch (error) {
      console.error("Error fetching payments details:", error);
      throw error;
    }
  },
  saveTransaction: async (currentTransaction, kioskId, token) => {
    try {
      const requestBody = {
        paymentData: currentTransaction,
        kioskId: kioskId,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/payments/add-transaction`,
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response;
    } catch (error) {
      console.error("Error adding transaction:", error);
      throw error;
    }
  },
  updatePaymentSatus: async (
    kioskId,
    totalAmount,
    currency,
    paymentStatus,
    token,
  ) => {
    if (!kioskId) {
      return null;
    }
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/payments/update`,
        {
          kioskId: kioskId,
          totalAmount: totalAmount,
          currency: currency,
          paymentStatus: paymentStatus,
        },

        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
    } catch (error) {
      console.error("Error updating payment status details:", error);
      throw error;
    }
  },
  getContractFileUrl: async (kioskId, token) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/kiosks/contracts/${kioskId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error(
        "Error fetching contract file URL:",
        error.response || error.message,
      );
      throw error;
    }
  },
  addEngagementStage: async (payload, token) => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/engagement/create`,
        payload,

        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
    } catch (error) {
      console.error(
        "Error fetching contract file URL:",
        error.response || error.message,
      );
      throw error;
    }
  },
  getDoctorEngagement: async (doctorId, token) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/engagement/timeline/${doctorId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data;
    } catch (error) {
      console.error(
        "Error fetching contract file URL:",
        error.response || error.message,
      );
      throw error;
    }
  },
  getReceiptFileUrl: async (kioskId, transactionId, token) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/payments/${kioskId}/transactions/${transactionId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data;
    } catch (error) {
      console.error(
        "Error fetching contract file URL:",
        error.response || error.message,
      );
      throw error;
    }
  },
  uploadReceipt: async (kioskId, transactionId, file, token) => {
    if (!file) throw new Error("File is required for uploading.");

    const formData = new FormData();
    formData.append("receipt", file);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/payments/${kioskId}/transactions/${transactionId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error uploading file:", error);
      throw error;
    }
  },

  uploadContractAPI: async (kioskId, file, token) => {
    if (!file) throw new Error("File is required for uploading.");

    const formData = new FormData();
    formData.append("contract", file);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/kiosks/contracts/${kioskId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error uploading file:", error);
      throw error;
    }
  },

  DeleteTransaction: async (kioskId, transactionId, token) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/payments/delete`,
        {
          kioskId,
          transactionId,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error deleting transaction entry:", error);
      throw error;
    }
  },
  fetchDoctorAndClinics: async (doctorKeyword, clinicKeyword, token) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/getAllDoctorsAndClinic`,
        {
          params: { doctorKeyword, clinicKeyword },

          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return res.data;
    } catch (error) {
      console.error("Error fetching doctos and clinics for dropdown", error);
      throw error;
    }
  },

  createTicket: async (payload, token) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/ticket`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error creating ticket:", error);
      throw error;
    }
  },

  searchDoctorsForTicket: async (keyword, clinicId, token) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/ticket/doctors-search`,
        {
          params: { keyword, clinicId },
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error searching doctors:", error);
      throw error;
    }
  },

  fetchSupportTickets: async (page = 1, limit = 40, search = "", token, status = "", priority = "", category = "") => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/ticket?page=${page}&limit=${limit}&search=${search}&status=${status}&priority=${priority}&category=${category}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching Tickets", error);
      throw error;
    }
  },

  changeTicketStatus: async (id, status, token) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/ticket/${id}`,
        { status }, // send status in body
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error changing ticket status:", error);
      throw error;
    }
  },

  fetchChat: async (id, token) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/ticket/${id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = res.data;
      return data;
    } catch (error) {
      console.error("Error fetching chats:", error);
      throw error;
    }
  },

  addTicketNote: async (id, text, token) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/ticket/${id}/notes`,
        { text },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error adding ticket note:", error);
      throw error;
    }
  },

  sendMessage: async (id, msg, token) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/ticket/${id}/messages`,
        {
          senderType: "support",
          message: msg,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch (error) {
      console.error("Error sending Message:", error);
      throw error;
    }
  },

  //subscription
  getSubscriptionsByDoctor: async (doctorId, token) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/v1/subscription/doctor/${doctorId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data;
    } catch (error) {
      console.error("Error finding Subscription for this doc", error);
      throw error;
    }
  },

  getPlans: async (token) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/v1/subscription/plan`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data;
    } catch (error) {
      console.error("Error finding Subscription for this doc", error);
      throw error;
    }
  },

  createSubscription: async (token, formdata) => {
    try {
      const resp = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/subscription/create`,
        formdata, // <-- body
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return resp;
    } catch (error) {
      console.error("Error creating subscription", error);
      throw error;
    }
  },
  renewSubscription: async (token, formdata) => {
    try {
      const resp = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/subscription/renew`,
        formdata, // <-- body
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return resp;
    } catch (error) {
      console.error("Error renew subscription", error);
      throw error;
    }
  },

  pauseSubscription: async (token, subscriptionId, reason) => {
    try {
      const resp = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/v1/subscription/pause`,
        { subscriptionId, reason },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return resp;
    } catch (error) {
      console.error("Error Canceling subscription", error);
      throw error;
    }
  },
  unpauseSubscription: async (token, subscriptionId, reason) => {
    try {
      const resp = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/v1/subscription/un-pause`,
        { subscriptionId, reason },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return resp;
    } catch (error) {
      console.error("Error Canceling subscription", error);
      throw error;
    }
  },

  overridePrice: async (token, subscriptionId, newPrice, reason) => {
    try {
      const resp = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/v1/subscription/override-price`,
        { subscriptionId, newPrice, reason },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return resp;
    } catch (error) {
      console.error("Error overriding subscription price", error);
      throw error;
    }
  },

  updateSubscriptionDates: async (token, subscriptionId, { startDate, endDate, reason }) => {
    try {
      const resp = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/v1/subscription/update-dates`,
        { subscriptionId, startDate, endDate, reason },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return resp;
    } catch (error) {
      console.error("Error updating subscription dates", error);
      throw error;
    }
  },

  swapSubscriptionPlans: async (token, { subscriptionIdA, subscriptionIdB, reason }) => {
    try {
      const resp = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/v1/subscription/swap-plans`,
        { subscriptionIdA, subscriptionIdB, reason },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return resp;
    } catch (error) {
      console.error("Error swapping subscription plans", error);
      throw error;
    }
  },
  requestTopDoctors: async ({ startDate, endDate, limit, email, token }) => {
    return axios.post(
      `${process.env.REACT_APP_API_HOST_URL}/v1/analytics/getTopDoctors/email`,
      {},
      {
        params: {
          startDate,
          endDate,
          limit,
          email,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
  },
  previewPlanChange: async (token, subscriptionId, newPlanId) => {
    try {
      const resp = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/preivew-change-plan`,
        {
          subscriptionId,
          newPlanId,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return resp;
    } catch (error) {
      console.error("Error previewing plan change", error);
      throw error;
    }
  },

  changePlan: async (token, formdata) => {
    try {
      const resp = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/change-plan`,
        formdata,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return resp;
    } catch (error) {
      console.error("Error changing plan", error);
      throw error;
    }
  },
  payNextInstallment: async (paymentId, token) => {
    try {
      const resp = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/v1/subscription/pay-installment`,
        { paymentId },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return resp;
    } catch (error) {
      console.error("Error paying installment", error);
      throw error;
    }
  },
  addTag: async ({ receiverId, module, redirectUrl, message, token }) => {
    return axios.post(
      `${process.env.REACT_APP_API_HOST_URL}/ops/v1/tags/tag`,
      {
        receiverId,
        module,
        redirectUrl,
        message,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
  },
  //Order
  fetchOrders: async (token, page, limit, keyword, status) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders`,
        {
          params: { page, limit, search: keyword, status },
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching orders:", error);
      throw error;
    }
  },

  getOrder: async (id, token) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching order:", error);
      throw error;
    }
  },

  fetchMockupPreview: async (id, token, forceDownload = false) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${id}/mockup-preview`,
        {
          params: { download: forceDownload },
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching mockup preview:", error);
      throw error;
    }
  },
  updateInvoice: async (id, quoteInput, token) => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${id}/invoice`,
        quoteInput,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error updating invoice:", error);
      throw error;
    }
  },
  fetchChecklistPhotos: async (id, token) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${id}/checklist-photos`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching checklist photos:", error);
      throw error;
    }
  },
  downloadQuote: async (id, token, forceDownload = false) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${id}/quote`,
        {
          params: { download: forceDownload },
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching quote:", error);
      throw error;
    }
  },
  downloadInvoice: async (id, token, forceDownload = false) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${id}/invoice`,
        {
          params: { download: forceDownload },
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching invoice:", error);
      throw error;
    }
  },
  createOrder: async (orderData, token) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/create`,
        orderData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error creating order:", error);
      throw error;
    }
  },

  toggleOrderAccountItem: async (id, key, token) => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${id}/accounts/${key}/toggle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error toggling account item:", error);
      throw error;
    }
  },
  markOrderInstalled: async (id, token) => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${id}/installation/mark-installed`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error marking installed:", error);
      throw error;
    }
  },
  fetchMyTasks: async (token) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/tasks/mine`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching tasks:", error);
      throw error;
    }
  },
  getAdvancePayment: async (id, token) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${id}/advance-payment`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching advance payment:", error);
      throw error;
    }
  },

  createAdvancePaymentLink: async (id, payload, token) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${id}/advance-payment-link`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error creating advance payment link:", error);
      throw error;
    }
  },
  recordManualAdvancePayment: async (id, payload, token) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${id}/advance-payment/manual`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error recording manual advance payment:", error);
      throw error;
    }
  },
  voidAdvancePayment: async (id, payload, token) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${id}/advance-payment/void`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error voiding advance payment:", error);
      throw error;
    }
  },
  fetchPaymentLinks: async (token) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/payment-links`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching payment links:", error);
      throw error;
    }
  },
  fetchClinicPaymentSummary: async (token, { page, limit, status, search } = {}) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/clinics/payment-summary`,
        { params: { page, limit, status, search }, headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching clinic payment summary:", error);
      throw error;
    }
  },
  fetchClinicPaymentLinks: async (clinicId, token) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/clinics/${clinicId}/payment-links`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching clinic payment links:", error);
      throw error;
    }
  },
  fetchAdvancePaymentActionItems: async (token, { page, limit } = {}) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/needs-action/advance-payments`,
        { params: { page, limit }, headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching needs action items:", error);
      throw error;
    }
  },
  fetchQuotations: async (token, { page, limit, status, search } = {}) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/quotations`,
        { params: { page, limit, status, search }, headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching quotations:", error);
      throw error;
    }
  },
  fetchQuotationActionItems: async (token, { page, limit } = {}) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/needs-action/quotations`,
        { params: { page, limit }, headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching quotation action items:", error);
      throw error;
    }
  },
  fetchEmiActionItems: async (token, { page, limit } = {}) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/needs-action/emi`,
        { params: { page, limit }, headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching EMI action items:", error);
      throw error;
    }
  },
  sendQuotation: async (id, note, token) => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${id}/quote/send`,
        { note },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error sending quotation:", error);
      throw error;
    }
  },
  sendEngageCredentialsAndMarkAccepted: async (id, note, token) => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${id}/quote/send-credentials-only`,
        { note },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error sending Engage credentials:", error);
      throw error;
    }
  },
  markQuoteAcceptedWithoutSending: async (id, token) => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${id}/quote/mark-accepted-no-send`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error marking quote accepted without sending:", error);
      throw error;
    }
  },
  checkOrderCreationConflicts: async (params, token) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/check-conflicts`,
        { params, headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error checking order creation conflicts:", error);
      throw error;
    }
  },
  updateQuote: async (id, quote, token, addresses = {}) => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${id}/quote`,
        { quote, ...addresses },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error updating quote:", error);
      throw error;
    }
  },
  updateOrderPaymentType: async (id, paymentType, token) => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${id}/quote/payment-type`,
        { paymentType },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error updating payment type:", error);
      throw error;
    }
  },
  setQuoteAccepted: async (id, accepted, token) => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${id}/quote-accepted`,
        { accepted },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error recording quote acceptance:", error);
      throw error;
    }
  },
  fetchAgreementActionItems: async (token, { page, limit } = {}) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/needs-action/agreements`,
        { params: { page, limit }, headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching agreement action items:", error);
      throw error;
    }
  },
  sendPurchaseAgreement: async (id, token) => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${id}/purchase-agreement/send`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error sending purchase agreement:", error);
      throw error;
    }
  },
  setAgreementSigned: async (id, signed, token) => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${id}/agreement-signed`,
        { signed },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error recording agreement signature:", error);
      throw error;
    }
  },
  uploadPurchaseAgreement: async (id, file, token) => {
    try {
      const form = new FormData();
      form.append("purchase_agreement", file);
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${id}/purchase-agreement/upload`,
        form,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error uploading purchase agreement:", error);
      throw error;
    }
  },
  downloadPurchaseAgreement: async (id, token, forceDownload = false) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${id}/purchase-agreement`,
        {
          params: { download: forceDownload },
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching purchase agreement:", error);
      throw error;
    }
  },
  opsUploadClinicLogo: async (id, file, token) => {
    try {
      const form = new FormData();
      form.append("clinic_logo", file);
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${id}/mockup/ops-upload-logo`,
        form,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error uploading clinic logo:", error);
      throw error;
    }
  },
  testUploadClinicLogo: async (id, file, token) => {
    try {
      const form = new FormData();
      form.append("clinic_logo", file);
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${id}/test-upload-clinic-logo`,
        form,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error uploading clinic logo:", error);
      throw error;
    }
  },
  testSelectOnboardingCallSlot: async (id, slot, token) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${id}/test-select-onboarding-call-slot`,
        { slot },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error selecting onboarding call slot:", error);
      throw error;
    }
  },
  resendAdvanceLink: async (id, token, dueDate, notify = true) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${id}/advance-payment-link/resend`,
        { ...(dueDate ? { dueDate } : {}), notify },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error resending advance link:", error);
      throw error;
    }
  },
  editDispatchDetails: async (id, payload, token) => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${id}/dispatch/edit`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error editing fulfilment details:", error);
      throw error;
    }
  },
  cancelPaymentLink: async (id, type, token) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${id}/payment-link/cancel`,
        { type },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error cancelling payment link:", error);
      throw error;
    }
  },
  reviseEmiPaymentTerms: async (id, payload, token) => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${id}/revise-emi-terms`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error revising EMI terms:", error);
      throw error;
    }
  },
  updateEmiStatus: async (id, status, reason, token) => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${id}/emi-status`,
        { status, reason },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error updating EMI status:", error);
      throw error;
    }
  },
  // "Deal Closed" messages from the sales Slack channel, waiting to become orders
  fetchSlackDeals: async (token, { status, page, limit } = {}) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/slack-deals`,
        { params: { status, page, limit }, headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching Slack deals:", error);
      throw error;
    }
  },
  dismissSlackDeal: async (id, token) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/slack-deals/${id}/dismiss`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error dismissing Slack deal:", error);
      throw error;
    }
  },
  // Clinic payout account review (see ml-service docs/payout-review-api.md)
  fetchPayoutAccounts: async (token, { status, search, page, limit } = {}) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/payout-accounts`,
        { params: { status, search, page, limit }, headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching payout accounts:", error);
      throw error;
    }
  },
  fetchPayoutSummary: async (token) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/payout-accounts/summary`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching payout summary:", error);
      throw error;
    }
  },
  fetchPayoutAccount: async (clinicId, token) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/payout-accounts/${clinicId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching payout account:", error);
      throw error;
    }
  },
  verifyPayoutAccountId: async (payload, token) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/payout-accounts/verify-account-id`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error verifying Razorpay account id:", error);
      throw error;
    }
  },
  // The plaintext bank account number. Callers must not log or store it.
  revealPayoutAccountNumber: async (clinicId, token) => {
    const response = await axios.post(
      `${process.env.REACT_APP_API_HOST_URL}/ops/v1/payout-accounts/${clinicId}/reveal-account-number`,
      {},
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data;
  },
  transitionPayoutAccount: async (clinicId, payload, token) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/payout-accounts/${clinicId}/transition`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error changing payout account status:", error);
      throw error;
    }
  },
  //Engagement Officer, onboarding calls
  fetchOnboardingCalls: async (token, { page, limit, status, search, owner } = {}) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/engagement-officer/onboarding-calls`,
        { params: { page, limit, status, search, owner }, headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching onboarding calls:", error);
      throw error;
    }
  },

  offerOnboardingSlots: async (orderId, payload, token) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/engagement-officer/onboarding-calls/${orderId}/offer-slots`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error offering onboarding slots:", error);
      throw error;
    }
  },

  logOnboardingFollowUp: async (orderId, payload, token) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/engagement-officer/onboarding-calls/${orderId}/follow-up`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error logging onboarding follow-up:", error);
      throw error;
    }
  },

  skipOnboardingCall: async (orderId, reason, token) => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/engagement-officer/onboarding-calls/${orderId}/skip`,
        { reason },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error skipping onboarding call:", error);
      throw error;
    }
  },

  reopenOnboardingCall: async (orderId, token) => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/engagement-officer/onboarding-calls/${orderId}/reopen`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error reopening onboarding call:", error);
      throw error;
    }
  },

  fetchDrafts: async (token) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/drafts`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching drafts:", error);
      throw error;
    }
  },

  discardDraft: async (orderId, token) => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/drafts/${orderId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error discarding draft:", error);
      throw error;
    }
  },

  fetchPipeline: async (token) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/pipeline`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching pipeline:", error);
      throw error;
    }
  },

  fetchStalledOrders: async (token) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/stalled-orders`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching stalled orders:", error);
      throw error;
    }
  },

  fetchSpecialists: async (token) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/specialists`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching specialists:", error);
      throw error;
    }
  },

  fetchZohoSync: async (token) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/zoho-sync`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching Zoho sync status:", error);
      throw error;
    }
  },

  //Section 4, purchase agreement
  approveAgreement: async (orderId, payload, token) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${orderId}/purchase-agreement/approve`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error approving agreement:", error);
      throw error;
    }
  },

  //Action item queue, shared by every internal role
  fetchActionItems: async (token) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/action-items`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching action items:", error);
      throw error;
    }
  },

  dismissActionItem: async (itemId, orderId, token) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/action-items/${itemId}/dismiss`,
        { orderId },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error dismissing action item:", error);
      throw error;
    }
  },

  //Section 6, pre-dispatch and dispatch
  fetchReadyToShip: async (token, { page, limit } = {}) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/ready-to-ship`,
        { params: { page, limit }, headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching ready to ship:", error);
      throw error;
    }
  },

  fetchShipments: async (token, { page, limit } = {}) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/shipments`,
        { params: { page, limit }, headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching shipments:", error);
      throw error;
    }
  },

  shareBalancePaymentLink: async (orderId, amount, dueDate, token) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${orderId}/balance-payment-link`,
        { amount, dueDate },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error sharing balance payment link:", error);
      throw error;
    }
  },

  resendBalanceLink: async (orderId, token, dueDate, notify = true) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${orderId}/balance-payment-link/resend`,
        { ...(dueDate ? { dueDate } : {}), notify },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error resending balance payment link:", error);
      throw error;
    }
  },

  uploadInvoice: async (orderId, file, token) => {
    try {
      const form = new FormData();
      form.append("invoice", file);
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${orderId}/invoice/upload`,
        form,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error uploading invoice:", error);
      throw error;
    }
  },

  generateInvoiceEarly: async (orderId, gstin, token) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${orderId}/invoice/generate`,
        { gstin },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error generating invoice:", error);
      throw error;
    }
  },

  emailInvoiceToDoctor: async (orderId, token) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${orderId}/invoice/email`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error emailing invoice:", error);
      throw error;
    }
  },

  refreshInvoice: async (orderId, token) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${orderId}/invoice/refresh`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error refreshing invoice:", error);
      throw error;
    }
  },

  setShippingDates: async (orderId, payload, token) => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${orderId}/shipping-dates`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error setting shipping dates:", error);
      throw error;
    }
  },

  markReadyToShip: async (orderId, payload, token) => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${orderId}/ready-to-ship`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error marking ready to ship:", error);
      throw error;
    }
  },

  resolveOrderHold: async (orderId, payload, token) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${orderId}/hold`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error resolving order hold:", error);
      throw error;
    }
  },

  confirmShipped: async (orderId, payload, token) => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${orderId}/shipped`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error confirming shipment:", error);
      throw error;
    }
  },

  //Section 6, accounts
  fetchReceipts: async (token) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/receipts`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching receipts:", error);
      throw error;
    }
  },

  reconcileReceipt: async (orderId, payload, token) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${orderId}/receipts/reconcile`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error reconciling receipt:", error);
      throw error;
    }
  },

  clearBalance: async (orderId, payload, token) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${orderId}/balance/clear`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error clearing balance:", error);
      throw error;
    }
  },

  raiseInvoice: async (orderId, payload, token) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${orderId}/invoice`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error raising invoice:", error);
      throw error;
    }
  },

  //Section 7, installation — list/fetch endpoints
  fetchShippedOrders: async (token, { page, limit } = {}) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/shipped-orders`,
        { params: { page, limit }, headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching shipped orders:", error);
      throw error;
    }
  },

  fetchOpsLeadSummary: async (token) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/ops-lead-summary`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching the Ops Lead summary:", error);
      throw error;
    }
  },
  fetchTechnicians: async (token) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/technicians`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching technicians:", error);
      throw error;
    }
  },

  fetchUnlinkedTechnicianAccounts: async (token) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/technicians/unlinked-accounts`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching unlinked technician accounts:", error);
      throw error;
    }
  },

  createTechnician: async (payload, token) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/technicians`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error creating technician:", error);
      throw error;
    }
  },

  setTechnicianActive: async (technicianId, active, token) => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/technicians/${technicianId}/active`,
        { active },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error updating technician status:", error);
      throw error;
    }
  },

  fetchTravelPlan: async (token) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/installations/travel-plan`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching travel plan:", error);
      throw error;
    }
  },

  fetchMyTravel: async (token, { page, limit, search } = {}) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/technician/travel`,
        { params: { page, limit, search }, headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching your travel:", error);
      throw error;
    }
  },

  fetchChecklists: async (token, { page, limit, search, status } = {}) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/technician/checklists`,
        { params: { page, limit, search, status }, headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching checklists:", error);
      throw error;
    }
  },

  fetchInaugurations: async (token, { page, limit, search } = {}) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/engagement-officer/inaugurations`,
        { params: { page, limit, search }, headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching inaugurations:", error);
      throw error;
    }
  },

  assignTechnician: async (orderId, payload, token) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${orderId}/installation/assign`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error assigning technician:", error);
      throw error;
    }
  },

  planTravel: async (orderId, payload, token) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${orderId}/installation/travel`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error planning travel:", error);
      throw error;
    }
  },

  confirmInauguration: async (orderId, payload, token) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${orderId}/inauguration/confirm`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error confirming inauguration:", error);
      throw error;
    }
  },

  respondToJob: async (orderId, payload, token) => {
    try {
      return await offlineQueue.queueOrSend("respondToJob", { ...payload, orderId, token }, sendRespondToJob);
    } catch (error) {
      console.error("Error responding to job:", error);
      throw error;
    }
  },

  submitActivationCode: async (orderId, payload, token) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${orderId}/installation/activate`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error submitting activation code:", error);
      throw error;
    }
  },

  completeInauguration: async (orderId, payload, token) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${orderId}/inauguration/complete`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error completing inauguration:", error);
      throw error;
    }
  },

  submitChecklist: async (orderId, payload, token) => {
    try {
      return await offlineQueue.queueOrSend("submitChecklist", { ...payload, orderId, token }, sendSubmitChecklist);
    } catch (error) {
      console.error("Error submitting checklist:", error);
      throw error;
    }
  },

  markIncomplete: async (orderId, payload, token) => {
    try {
      return await offlineQueue.queueOrSend("markIncomplete", { ...payload, orderId, token }, sendMarkIncomplete);
    } catch (error) {
      console.error("Error marking installation incomplete:", error);
      throw error;
    }
  },

  //Section 7, engagement officer
  assignInfluencer: async (orderId, payload, token) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${orderId}/inauguration/influencer`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error assigning influencer:", error);
      throw error;
    }
  },

  confirmCallAttendance: async (orderId, payload, token) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/engagement-officer/onboarding-calls/${orderId}/confirm`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error confirming call attendance:", error);
      throw error;
    }
  },

  acceptHandover: async (orderId, payload, token) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${orderId}/handover/accept`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error accepting handover:", error);
      throw error;
    }
  },

  introduceToCustomer: async (orderId, payload, token) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/orders/${orderId}/handover/introduce`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error sending introduction:", error);
      throw error;
    }
  },

  //Section 8 — expense claims
  // payload: { orderId, submit, items: [{category, categoryDetail, amountPaise, date, description}] }
  // Plain JSON — receipt upload is off for now.
  createExpenseClaim: async (payload, token) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/expenses`,
        {
          orderId: payload.orderId,
          submit: Boolean(payload.submit),
          items: payload.items,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error filing expense claim:", error);
      throw error;
    }
  },

  fetchMyExpenses: async (token, { page = 1, limit = 15, month, year } = {}) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/expenses/mine`,
        { params: { page, limit, month, year }, headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching my expenses:", error);
      throw error;
    }
  },

  fetchExpenseQueue: async (token, { page = 1, limit = 15, status } = {}) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/expenses/queue`,
        { params: { page, limit, status }, headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching expense approval queue:", error);
      throw error;
    }
  },

  approveExpenseClaim: async (claimId, token) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/expenses/${claimId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error approving expense claim:", error);
      throw error;
    }
  },

  rejectExpenseClaim: async (claimId, reason, token) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/expenses/${claimId}/reject`,
        { reason },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error rejecting expense claim:", error);
      throw error;
    }
  },

  queryExpenseClaim: async (claimId, note, token) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/expenses/${claimId}/query`,
        { note },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error querying expense claim:", error);
      throw error;
    }
  },

  reverseExpenseClaim: async (claimId, reason, token) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/expenses/${claimId}/reverse`,
        { reason },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error reversing expense claim:", error);
      throw error;
    }
  },

  payExpenseClaims: async (claimIds, reference, token) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST_URL}/ops/v1/expenses/pay`,
        { claimIds, reference },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      return response.data;
    } catch (error) {
      console.error("Error running expense payment:", error);
      throw error;
    }
  },
};

export default apiService;